import { reactEvents } from '@bridge/reactEvents';
import { articlesApi } from '@texas/api/endpoints/articlesApi';
import { MatrixAxis } from '@texas/api/endpoints/compositionGroup/compositionGroupMatrixApi';
import { articleTemplatesApi } from '@texas/api/endpoints/articleTemplatesApi';
import {
  contactsApi,
  defaultSearchQuery,
} from '@texas/api/endpoints/contactsApi';
import {
  CustomerComplianceDocument,
  customersApi,
} from '@texas/api/endpoints/customersApi';
import { matrixConfigurationApi } from '@texas/api/endpoints/matrixConfigurationApi';
import { complianceDocumentDefinitionsApi } from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { useActiveSession } from '@texas/hooks/useSession';
import { WarningItem } from '@texas/components/shared/WarningSummary';
import { ReactSelectOption } from '@texas/types';
import { createContextHookValue } from '@texas/utils/contextState';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  getActiveWarnings,
  getArticleWarnings,
  getFormWarnings,
} from './warnings';
import {
  buildArticleNumbers,
  getArticleNumberLimits,
} from '../numbering/articleNumbers';
import { useCustomerSearch } from '../copy/useCustomerSearch';
import { ProposedArticleOptions } from './ProposedArticleCard';

import { useArticleAvailability } from '../numbering/useArticleAvailability';
import { useArticleClashes } from '../numbering/useArticleClashes';

interface CreateArticleForm {
  customer: ReactSelectOption | null;
  conceptId: number | null;
  branchIds: number[];
  branchCertificates: Record<number, number[] | undefined>;
  itemCode: string;
  templateId: number | null;
  name: string;
  contactId: number | null;
  variable: boolean;
  collectionItem: boolean;
  mode: 'single' | 'range';
  start: number;
  end: number;
  padding: number;
  position: 'before' | 'after';
  skipUnavailable: boolean;
  options: Record<string, ProposedArticleOptions | undefined>;
}

export interface CreateArticleProps {
  customerId?: number;
  conceptId?: number;
}

function customerDocumentIdsForBranch(
  documents: CustomerComplianceDocument[],
  branchId: number,
) {
  return documents
    .filter(
      (document) =>
        !document.archived &&
        (!document.branches.length ||
          document.branches.some((branch) => branch.id === branchId)),
    )
    .map((document) => document.complianceDocumentDefinitionId);
}

function useCreateArticleValue(props: CreateArticleProps) {
  const warningScope = useId();
  const [expandedArticles, setExpandedArticles] = useState<
    Partial<Record<string, boolean>>
  >({});
  const revealWarningGroup = useCallback((groupId?: string) => {
    if (groupId)
      setExpandedArticles((current) => ({ ...current, [groupId]: true }));
  }, []);
  const { currentUser } = useActiveSession();
  const { t } = useTranslation();
  const search = useCustomerSearch();
  const { watch, setValue, getValues } = useForm<CreateArticleForm>({
    defaultValues: {
      customer: null,
      conceptId: props.conceptId ?? null,
      branchIds: [],
      branchCertificates: {},
      itemCode: '',
      templateId: null,
      name: '',
      contactId: null,
      variable: false,
      collectionItem: false,
      mode: 'single',
      start: 1,
      end: 5,
      padding: 3,
      position: 'after',
      skipUnavailable: false,
      options: {},
    },
  });
  const {
    customer,
    conceptId,
    branchIds,
    branchCertificates,
    itemCode,
    templateId,
    name,
    contactId,
    variable,
    collectionItem,
    mode,
    start,
    end,
    padding,
    position,
    options,
    skipUnavailable,
  } = watch();

  const metadata = useMetadata();
  const customerId = Number(customer?.value) || 0;
  const templates = useApiResource(articleTemplatesApi.getAll);
  const [branchesData, refetchBranches, branchesLoading, , branchesError] =
    metadata.branchesData;
  const branches = {
    data: branchesData,
    loading: branchesLoading,
    error: branchesError,
  };
  const [regionsData, refetchRegions, regionsLoading, , regionsError] =
    metadata.branchRegionsData;
  const regions = {
    data: regionsData,
    loading: regionsLoading,
    error: regionsError,
  };
  const certificates = useApiResource(complianceDocumentDefinitionsApi.getAll);
  const customerDocuments = useApiResource(customersApi.getComplianceDocuments);
  const [productsData, refetchProducts, productsLoading, , productsError] =
    metadata.productGroupsData;
  const products = {
    data: productsData,
    loading: productsLoading,
    error: productsError,
  };
  const concepts = useApiResource(customersApi.getConcepts);
  const contacts = useApiResource(contactsApi.getAll);
  const initialCustomer = useApiResource(customersApi.getSlim);
  const nodes = useApiResource(matrixConfigurationApi.getNodes);
  const { refetch: refetchTemplates } = templates;
  const { refetch: refetchNodes } = nodes;
  const { refetch: refetchCertificates } = certificates;
  const { refetch: refetchCustomerDocuments, set: setCustomerDocuments } =
    customerDocuments;
  const { refetch: refetchInitialCustomer } = initialCustomer;
  const { refetch: refetchConcepts, set: setConcepts } = concepts;
  const { refetch: refetchContacts, set: setContacts } = contacts;
  const creationRequest = useApiRequest(articlesApi.createArticles);
  const numberedTargets = useMemo(
    () =>
      buildArticleNumbers(mode, itemCode, '', start, end, padding, position),
    [mode, itemCode, start, end, padding, position],
  );
  const previousNumberedTargets = useRef(numberedTargets);
  useEffect(() => {
    const previous = previousNumberedTargets.current;
    previousNumberedTargets.current = numberedTargets;
    const current = getValues('options');
    const next = { ...current };
    let changed = false;
    for (const [index, target] of numberedTargets.entries()) {
      if (current[target.itemCode] || index >= previous.length) continue;
      const option = current[previous[index].itemCode];
      if (!option) continue;
      next[target.itemCode] = { ...option, uploading: false };
      changed = true;
    }
    if (changed) setValue('options', next);
  }, [numberedTargets, getValues, setValue]);
  const targets = useMemo(
    () => numberedTargets.map((target) => ({ ...target, name: name.trim() })),
    [numberedTargets, name],
  );
  const codes = useMemo(
    () => numberedTargets.map((target) => target.itemCode),
    [numberedTargets],
  );
  const defaultTemplate = useMemo(
    () =>
      templates.data
        ?.filter((item) => !item.archived)
        .sort((a, b) => a.displayOrder - b.displayOrder)[0],
    [templates.data],
  );
  const template =
    templateId == null
      ? defaultTemplate
      : templates.data?.find(
          (item) => !item.archived && item.id === templateId,
        );
  const itemCodeMinLength = template?.itemCodeMinLength ?? 0;
  const {
    maximumLength: itemCodeMaxLength,
    reservedDigits: rangeNumberLength,
    inputMaxLength: itemCodeInputMaxLength,
  } = getArticleNumberLimits(
    mode,
    start,
    end,
    padding,
    template?.itemCodeMaxLength,
  );
  const availability = useArticleAvailability(codes);
  const proposals = useMemo(
    () =>
      targets.map((target) => {
        const option = options[target.itemCode] ?? {};
        const xNode = nodes.data?.find(
          (node) => node.id === template?.matrixNodeXId,
        );
        const yNode = nodes.data?.find(
          (node) => node.id === template?.matrixNodeYId,
        );
        const defaultColorAxis = yNode ? MatrixAxis.Rows : null;
        const colorAxis =
          option.colorAxis === undefined ? defaultColorAxis : option.colorAxis;
        return {
          ...target,
          options: {
            ...option,
            colorAxis,
          },
          unavailableInTexas:
            availability.data?.[target.itemCode]?.errorType ===
              'InUseByTexas' ||
            availability.data?.[target.itemCode]?.errorType ===
              'ArchivedInTexas',
          warnings: getArticleWarnings(
            {
              options: { ...option, colorAxis },
              products: products.data,
              xNode,
              yNode,
              erpConflict:
                availability.data?.[target.itemCode]?.errorType ===
                'InUseByErp',
            },
            t,
            `${warningScope}.article.${target.itemCode}`,
          ),
          template,
          xNode,
          yNode,
          invalidLength:
            target.itemCode.length < itemCodeMinLength ||
            target.itemCode.length > itemCodeMaxLength,
        };
      }),
    [
      targets,
      options,
      template,
      nodes.data,
      itemCodeMinLength,
      itemCodeMaxLength,
      products.data,
      availability.data,
      t,
      warningScope,
    ],
  );
  const itemCodeLengthInvalid = proposals.some(
    (target) => target.invalidLength,
  );

  const onIgnoreClashesChange = useCallback(
    (value: boolean) => setValue('skipUnavailable', value),
    [setValue],
  );
  const isErpImportSelected = useCallback(
    (code: string) => !!options[code]?.importErp,
    [options],
  );
  const { firstArticleClashes, suggestedRange, creationTargets, ignoredCount } =
    useArticleClashes({
      targets: proposals,
      availability,
      mode,
      itemCode,
      start,
      end,
      padding,
      position,
      minimumLength: itemCodeMinLength,
      maximumLength: itemCodeMaxLength,
      ignoreClashes: skipUnavailable,
      onIgnoreClashesChange,
      allowErpImport: true,
      isErpImportSelected,
    });

  useEffect(() => {
    void refetchTemplates(currentUser.organizationId);
    void refetchNodes(currentUser.organizationId);
    void refetchBranches();
    void refetchRegions('');
    void refetchProducts();
    if (currentUser.betaFeatures)
      void refetchCertificates({
        searchTerm: '',
        includeArchived: false,
        sortBy: 'name',
        sortDesc: false,
      });
  }, [
    currentUser.organizationId,
    refetchTemplates,
    refetchNodes,
    refetchBranches,
    refetchRegions,
    refetchProducts,
    refetchCertificates,
    currentUser.betaFeatures,
  ]);
  useEffect(() => {
    if (props.customerId) void refetchInitialCustomer(props.customerId);
  }, [props.customerId, refetchInitialCustomer]);
  useEffect(() => {
    if (initialCustomer.data) {
      if (Number(getValues('customer')?.value) !== initialCustomer.data.id)
        setCustomerDocuments(null);
      setValue('customer', {
        value: initialCustomer.data.id,
        label: initialCustomer.data.name,
      });
    }
  }, [initialCustomer.data, getValues, setCustomerDocuments, setValue]);
  useEffect(() => {
    setConcepts(null);
    setContacts(null);
    if (!customerId) return;
    void refetchConcepts(customerId);
    void refetchContacts({
      ...defaultSearchQuery,
      customerId,
      pageSize: 1000,
    });
  }, [customerId, refetchConcepts, setConcepts, refetchContacts, setContacts]);
  useEffect(() => {
    setCustomerDocuments(null);
    setValue('branchCertificates', {});
    if (customerId && currentUser.betaFeatures)
      void refetchCustomerDocuments(customerId);
  }, [
    customerId,
    currentUser.betaFeatures,
    refetchCustomerDocuments,
    setCustomerDocuments,
    setValue,
  ]);
  useEffect(() => {
    if (!customerDocuments.data) return;
    const current = getValues('branchCertificates');
    const selected = new Set(branchIds);
    const changed =
      branchIds.some((id) => current[id] === undefined) ||
      Object.keys(current).some((id) => !selected.has(Number(id)));
    if (!changed) return;
    const next: Record<number, number[]> = {};
    for (const id of branchIds)
      next[id] =
        current[id] ?? customerDocumentIdsForBranch(customerDocuments.data, id);
    setValue('branchCertificates', next);
  }, [branchIds, customerDocuments.data, getValues, setValue]);
  useEffect(
    () =>
      reactEvents.newContactAddedToBrand.subscribe(() => {
        if (customerId)
          void refetchContacts({
            ...defaultSearchQuery,
            customerId,
            pageSize: 1000,
          });
      }),
    [customerId, refetchContacts],
  );
  const resources = {
    templates,
    branches,
    regions,
    certificates,
    products,
    nodes,
    concepts,
    contacts,
    initialCustomer,
  };
  const loading = Object.values(resources).some((r) => r.loading);
  const errors = Object.values(resources).flatMap((r) =>
    r.error ? [r.error] : [],
  );
  const hasNew = creationTargets.some(
    (target) => availability.data?.[target.itemCode]?.isValid,
  );
  const fieldWarnings = getFormWarnings(
    {
      customerId,
      hasTemplate: !!template,
      itemCode,
      invalidLength: itemCodeLengthInvalid,
      minimumLength: itemCodeMinLength,
      maximumLength: itemCodeMaxLength,
      name,
      hasNewArticles: hasNew,
      branchIds,
      mode,
      targetCount: targets.length,
    },
    t,
    `${warningScope}.form`,
  );
  const warningItems = [
    ...getActiveWarnings(fieldWarnings, 'form'),
    ...(firstArticleClashes
      ? [
          {
            id: 'form.firstArticleClash',
            targetId: fieldWarnings.itemCode.targetId,
            message: t(
              mode === 'range'
                ? 'article.create.firstArticleClash'
                : 'article.create.singleArticleClash',
              { itemCode: proposals[0].itemCode },
            ),
            mandatory: true,
          },
        ]
      : []),
    ...creationTargets.flatMap<WarningItem>((target) =>
      target.unavailableInTexas
        ? [
            {
              id: `article.${target.itemCode}.unavailable`,
              targetId: fieldWarnings.itemCode.targetId,
              message: t('article.create.unavailableArticle', {
                itemCode: target.itemCode,
              }),
              mandatory: true,
            },
          ]
        : getActiveWarnings(target.warnings, `article.${target.itemCode}`).map(
            (warning) => ({
              ...warning,
              groupId: target.itemCode,
              message: t('article.create.warningForArticle', {
                itemCode: target.itemCode,
                message: warning.message,
              }),
            }),
          ),
    ),
  ];
  function validateCreation() {
    if (
      firstArticleClashes ||
      warningItems.some((warning) => warning.mandatory)
    )
      return false;
    if (!creationTargets.length) return false;
    if (availability.loading || availability.error) return false;
    if (loading || errors.length || creationRequest.loading) return false;
    for (const proposal of creationTargets) {
      const { itemCode, template, invalidLength, options: option } = proposal;
      if (!template || invalidLength) return false;
      const result = availability.data?.[itemCode];
      if (option.uploading) return false;
      if (result?.isValid) {
        continue;
      }
      if (result?.errorType !== 'InUseByErp') return false;
      if (!option.importErp) return false;
    }
    return true;
  }
  function applyConfigurationToAll(itemCode: string) {
    if (!availability.data || availability.loading || availability.error)
      return;
    const source = creationTargets.find(
      (target) => target.itemCode === itemCode,
    );
    if (!source?.template || source.unavailableInTexas) return;
    const current = getValues('options');
    const next = { ...current };
    for (const target of creationTargets) {
      if (target.unavailableInTexas) continue;
      next[target.itemCode] = {
        ...current[target.itemCode],
        productGroupId: source.options.productGroupId,
        nodeXValues:
          source.options.nodeXValues?.map((value) => ({ ...value })) ?? [],
        nodeYValues:
          source.options.nodeYValues?.map((value) => ({ ...value })) ?? [],
        colorAxis: source.options.colorAxis ?? null,
      };
    }
    setValue('options', next);
  }
  function applySuggestedRange() {
    if (
      !firstArticleClashes ||
      !suggestedRange.data ||
      suggestedRange.loading ||
      suggestedRange.error
    )
      return;
    const nextTargets = buildArticleNumbers(
      'range',
      itemCode,
      name,
      suggestedRange.data.start,
      suggestedRange.data.end,
      padding,
      position,
    );
    const currentOptions = getValues('options');
    setValue('options', {
      ...currentOptions,
      ...Object.fromEntries(
        nextTargets.map((target, index) => [
          target.itemCode,
          { ...currentOptions[proposals[index].itemCode], uploading: false },
        ]),
      ),
    });
    setValue('start', suggestedRange.data.start);
    setValue('end', suggestedRange.data.end);
  }
  const canSubmit = validateCreation();
  function selectTemplate(id: number) {
    if (id === template?.id) return;
    setValue('templateId', id);
    setValue(
      'options',
      Object.fromEntries(
        Object.entries(getValues('options')).map(([code, option]) => [
          code,
          { ...option, nodeXValues: [], nodeYValues: [], colorAxis: undefined },
        ]),
      ),
    );
  }
  const selectCustomer = (value: ReactSelectOption | null) => {
    if (Number(value?.value) !== customerId) setCustomerDocuments(null);
    setValue('customer', value);
    setValue('conceptId', null);
    setValue('contactId', null);
  };
  const submit = async () => {
    if (!canSubmit) return;
    const response = await creationRequest.request(
      customerId,
      creationTargets.map((target) => ({
        itemCode: target.itemCode,
        name: target.name,
        conceptId,
        articleTemplateId: template!.id,
        productGroupId: target.options.productGroupId!,
        nodeXValues: target.options.nodeXValues ?? [],
        nodeYValues: target.options.nodeYValues ?? [],
        colorAxis: target.options.colorAxis ?? null,
        contactId,
        branches: branchIds.map((id) => ({ id })),
        variable,
        collectionItem,
        branchCertificates: currentUser.betaFeatures
          ? branchIds.map((branchId) => ({
              branchId,
              certificateDefinitionIds: branchCertificates[branchId] ?? [],
            }))
          : [],
        layoutFileId: options[target.itemCode]?.layout?.id ?? null,
        force:
          availability.data?.[target.itemCode]?.errorType === 'InUseByErp' &&
          !!options[target.itemCode]?.importErp,
        masterBranchId: options[target.itemCode]?.masterBranchId ?? null,
      })),
    );
    if (response.hasError) return;
    if (response.data.articleId)
      reactEvents.routingChanged.dispatch({
        to: 'article',
        navObj: { articleId: response.data.articleId },
      });
    else
      reactEvents.routingChanged.dispatch({
        to: 'jobs.detail',
        navObj: { jobId: response.data.jobId },
      });
  };
  return {
    setValue,
    getValues,
    skipUnavailable,
    firstArticleClashes,
    suggestedRange,
    applySuggestedRange,
    creationTargets,
    ignoredCount,
    customer,
    conceptId,
    branchIds,
    branchCertificates,
    itemCode,
    template,
    itemCodeMinLength,
    itemCodeMaxLength,
    itemCodeInputMaxLength,
    itemCodeLengthInvalid,
    rangeNumberLength,
    selectTemplate,
    name,
    contactId,
    variable,
    collectionItem,
    mode,
    start,
    end,
    padding,
    position,
    options,
    resources,
    search,
    customerId,
    creationRequest,
    targets,
    proposals,
    applyConfigurationToAll,
    availability,
    loading,
    errors,
    canSubmit,
    fieldWarnings,
    warningItems,
    expandedArticles,
    setExpandedArticles,
    revealWarningGroup,
    selectCustomer,
    submit,
    refetchInitialCustomer,
    setConcepts,
  };
}

export const [CreateArticleProvider, useCreateArticle] = createContextHookValue(
  useCreateArticleValue,
);
